import { integer, jsonb, pgTable, text, timestamp, uuid, varchar } from 'drizzle-orm/pg-core';
import { id, timestamps } from './_shared';
import { users } from './users';

// =============================================================================
// ALIGNMENT ESSAY — "Why [Company]? / why we'd work well together"
// (docs/design/alignment-essay-module-2026-07-05.md + Jason's build-go
// additions at that doc's tail — assembly-over-invention + the word-count
// selector). A THIRD tailored artifact beside résumé + cover letter: a
// 250-400-word (user-adjustable target/max), first-person essay weaving (1)
// what drives THIS user (DNA origins/through-line/positioning spine +
// declared direction/goals) with (2) what the company VERIFIABLY publishes
// about its values/culture. Written in the USER'S voice speaking TO the
// company — Mira is the ghost-helper, never the narrator.
//
// TWO TABLES, mirroring drive_export.ts's OWN two-table shape exactly (the
// newest precedent this lane follows, per CLAUDE.md's corrected 2026-06-22
// law: identity = per-user users.id uuid; a NEW table takes the CURRENT-LAW
// uuid FK shape from day one rather than perpetuating the resumes/jobs/
// coverLetters-era text convention — same posture entitlement_grants.ts,
// presence_profiles.ts, and drive_export.ts's own userId column all take):
//
//   company_culture_sources — the SECOND anti-invention pillar for this
//     module: a verified-source-lake row (fetched/pasted culture material,
//     url + retrieved_at + a content_hash). A SHARED lake (no per-user
//     scoping on reads — company values are a fact about the COMPANY, not
//     the user; a pasted row records WHO pasted it via pasted_by_user_id,
//     null = fetched by the app). Mirrors field_practices.ts's own
//     provenance discipline (a real citation or the row does not exist).
//
//   alignment_essays — one row per generated run, append-only (regen sets
//     superseded_by on the PRIOR row rather than ever deleting it — the
//     nothing-unrecoverable law, same posture resumes.immutable/
//     coverLetters.immutable already establish for this module's siblings).
//     job_id is text — jobs.jobId is TEXT everywhere it is referenced
//     (jobScores.jobId, tailoringRuns.jobId, resumes.jobId, coverLetters.
//     jobId all read `text('job_id')` even though jobs.id itself is a uuid
//     primary key — this table matches that EXISTING, verified precedent
//     rather than introducing a new, inconsistent uuid FK for the same
//     logical reference).
//
// ADDITIVE ONLY (rule #11): CREATE TABLE IF NOT EXISTS via the Neon tool
// (docs/design/alignment-essay-neon.sql), mirrored here in Drizzle and
// exported from index.ts. This file is NOT applied by this lane — the SQL
// file is the deliverable; a human/a later lane runs it against Neon.
// =============================================================================

/**
 * A verified company-culture source — a fetched careers/values page, OR a
 * pasted excerpt the user supplied when no derivable URL existed. SHARED
 * across users (no userId column, no per-user read scoping): a company's
 * published values are a fact about the company, not about any one user's
 * corpus — the SAME row can ground an essay for every user targeting that
 * company, exactly as platform_algorithm_rules / au_visa_data / craft_methods
 * are shared, cross-user ponds. NO INVENTION: content_excerpt is the REAL
 * stored material (fetched HTML sanitized to text, or the user's own paste)
 * — never a summary or a paraphrase the model produced, so every essay that
 * cites this row cites something a human can independently verify at `url`.
 */
export const companyCultureSources = pgTable('company_culture_sources', {
  id: id(),
  companyName: text('company_name').notNull(),
  /** The careers/values page URL this was fetched from. NULL for a pasted
   *  row with no source URL on hand (the user pasted raw text with nothing
   *  to cite) — never a fabricated URL standing in for "I don't have one." */
  url: text('url'),
  retrievedAt: timestamp('retrieved_at', { withTimezone: true }),
  /** The stored verified material itself — sanitized fetched text, or the
   *  user's pasted excerpt verbatim. This is what the prompt fences inside
   *  <untrusted_input> and what the essay's provenance line points at. */
  contentExcerpt: text('content_excerpt').notNull(),
  /** SHA-256 of contentExcerpt (hex) — cheap dedup key so a re-fetch of the
   *  same page (or a re-paste of the same text) can recognize an existing
   *  row rather than accumulating identical duplicates; never used to DROP
   *  a row (nothing-unrecoverable) — only to prefer reusing an existing
   *  match on write. */
  contentHash: varchar('content_hash', { length: 64 }),
  /** 'values_page' | 'careers_page' | 'job_posting' | 'pasted' — how this
   *  material entered the lake. Determines the provenance line's phrasing
   *  ("fetched from their careers page" / "grounded in what {company} says
   *  about itself in this posting" / "the values text you pasted") — see
   *  AlignmentEssayService.formatCultureProvenanceLine, the one place this
   *  wording is computed. 'job_posting' (added 2026-07-05) = extracted from
   *  the job's OWN stored description text (an "About {Company}" / mission
   *  / values block already inside the posting the applicant is applying
   *  to) — no fetch, no paste, purely a read of already-served data. */
  sourceKind: varchar('source_kind', { length: 16 }).notNull(),
  /** Who pasted this excerpt, when sourceKind = 'pasted'. NULL means fetched
   *  by the app (an auto-fetch has no pasting user) — never a placeholder
   *  actor. Nullable FK: a user deletion cascades this to null (SET NULL,
   *  not a cascade delete) so the shared lake row survives even if the
   *  pasting user's account is later removed — the material remains
   *  verifiable/reusable for every OTHER user targeting this company. */
  pastedByUserId: uuid('pasted_by_user_id').references(() => users.id, { onDelete: 'set null' }),
  ...timestamps,
});

/**
 * One generated alignment-essay run. Append-only regen chain: a regen (the
 * "tell me what to change" react-to-refine loop) inserts a NEW row and sets
 * supersededBy on the PRIOR row's id — the prior body is never overwritten
 * or deleted (nothing-unrecoverable), mirroring resumes/coverLetters'
 * immutable-snapshot-per-version discipline.
 */
export const alignmentEssays = pgTable('alignment_essays', {
  id: id(),
  userId: uuid('user_id')
    .notNull()
    .references(() => users.id, { onDelete: 'cascade' }),
  /** text, matching jobs.jobId's type everywhere else it is referenced
   *  (jobScores/tailoringRuns/resumes/coverLetters) — see this file's header. */
  jobId: text('job_id').notNull(),
  /** Which company_culture_sources row grounded this run, or null when the
   *  run proceeded DRIVERS-ONLY (no verifiable company source existed at
   *  generation time — the honest degraded state, never a fabricated
   *  company-values claim). */
  cultureSourceId: uuid('culture_source_id').references(() => companyCultureSources.id),
  body: text('body').notNull(),
  wordCount: integer('word_count').notNull(),
  /** The user-set target length for THIS run (default 250) — feeds the
   *  brevity budget's `words` ask; the model is asked to land near this,
   *  not merely to stay under maxWords. */
  targetWords: integer('target_words').notNull(),
  /** The user-set hard ceiling for THIS run (default 400) — ENFORCED IN
   *  CODE by the brevity gate (analyzeText), never merely requested in the
   *  prompt. A body still over maxWords after one revision pass ships the
   *  honest over-budget state (blocked, never silently rendered long). */
  maxWords: integer('max_words').notNull(),
  /** Optional free-text tone guidance from the "tell me what to change"
   *  react-to-refine loop — null on a first-generation run. */
  toneNotes: text('tone_notes'),
  /** Mint-context stamp — { sourceEpoch, origin: 'user', basedOnId?,
   *  cultureSourceMissing? } — mirrors resumes.provenance / coverLetters.
   *  provenance exactly (jsonb, nullable, never fabricated when absent). */
  provenance: jsonb('provenance'),
  /** The render-blocking LintGate's own verdict (brevity + ai_tell) —
   *  mirrors coverLetters.linterReport's shape/purpose for this artifact. */
  linterReport: jsonb('linter_report'),
  /** The grounding gate's verdict over this essay's person-claims (mirrors
   *  TailorResponse's additive `grounding` field) — { grounded, flags }. */
  groundingReport: jsonb('grounding_report'),
  /** The prior alignment_essays row this run supersedes-FROM's own id, set
   *  on THAT prior row once THIS row lands (see this file's header) — never
   *  set on insert for a first-generation run (null). */
  supersededBy: uuid('superseded_by'),
  ...timestamps,
});
